import * as React from "react";
// @ts-ignore
import * as SelectPrimitive from "@radix-ui/react-select";
import { AlertTriangle, Check, ChevronDown, Loader2, Minus, X } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export interface DropdownOption {
  value: string;
  label: string;
  dot?: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  disabledReason?: string;
  badge?: React.ReactNode;
}

interface DropdownProps {
  value: string;
  onChange: (value: string) => void;
  options: readonly DropdownOption[];
  placeholder?: string;
  showPlaceholderWhenEmpty?: boolean;
  leadingLabel?: string;
  className?: string;
  placeholderClassName?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  align?: "start" | "center" | "end";
  variant?: "pill" | "form";
  searchable?: boolean;
  checkboxStyle?: boolean;
  loading?: boolean;
  chunkSize?: number;
  error?: boolean;
  clearable?: boolean;
}

const SIZE_CLASS = {
  sm: "h-[38px] text-sm px-3",
  md: "h-[38px] text-sm px-3.5",
} as const;

const VARIANT_CLASS = {
  pill: "rounded-lg border border-border bg-card text-foreground hover:border-indigo-400/60 dark:hover:border-indigo-400/40 focus-visible:border-indigo-500 dark:focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/20",
  form: "rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] text-[#0f172a] dark:text-white hover:border-indigo-400/60 dark:hover:border-indigo-400/40 focus-visible:border-indigo-500 dark:focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-indigo-500/10 w-full",
} as const;

const EMPTY_VALUE_TOKEN = "__dropdown_empty__";
const fromRadix = (v: string) => (v === EMPTY_VALUE_TOKEN ? "" : v);

export function SelectedLabel({
  label,
  className = "truncate",
  contentClassName = "",
}: {
  label: string;
  className?: string;
  contentClassName?: string;
}) {
  const ref = React.useRef<HTMLSpanElement>(null);
  const [open, setOpen] = React.useState(false);
  const isOverflowing = () => {
    const el = ref.current;
    return !!el && el.scrollWidth > el.clientWidth + 1;
  };
  return (
    <Tooltip
      open={open}
      onOpenChange={(next: boolean) => setOpen(next ? isOverflowing() : false)}
    >
      <TooltipTrigger asChild>
        <span ref={ref} className={className}>
          {label}
        </span>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        sideOffset={6}
        className={`max-w-xs break-words ${contentClassName}`}
      >
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function DropdownItem({
  opt,
  value,
  checkboxStyle,
  clearable,
  onDeselect,
}: {
  opt: DropdownOption;
  value: string;
  checkboxStyle?: boolean;
  clearable?: boolean;
  onDeselect?: () => void;
}) {
  const [open, setOpen] = React.useState(false);
  const isSelected = opt.value === value;

  const item = (
    <SelectPrimitive.Item
      key={opt.value}
      value={opt.value === "" ? EMPTY_VALUE_TOKEN : opt.value}
      disabled={opt.disabled}
      onPointerEnter={() => {
        if (opt.disabledReason) setOpen(true);
      }}
      onPointerLeave={() => {
        if (opt.disabledReason) setOpen(false);
      }}
      onPointerDown={(e: React.PointerEvent) => {
        if (opt.disabledReason) {
          e.preventDefault();
          e.stopPropagation();
          setOpen((prev) => !prev);
        }
      }}
      onPointerUp={(e: React.PointerEvent) => {
        if (opt.disabledReason) return;
        if (clearable && isSelected) {
          e.preventDefault();
          onDeselect?.();
        }
      }}
      onClick={(e: React.MouseEvent) => {
        if (opt.disabledReason) {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
          return;
        }
        if (clearable && isSelected) {
          e.preventDefault();
          onDeselect?.();
        }
      }}
      onKeyDown={(e: React.KeyboardEvent) => {
        if (opt.disabledReason) return;
        if (clearable && isSelected && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onDeselect?.();
        }
      }}
      className={`group/item relative flex w-full cursor-pointer select-none items-center gap-2.5 rounded-lg px-2.5 py-1.5 ${opt.badge ? "pr-2.5" : "pr-8"} text-sm text-foreground outline-none transition-colors break-words data-[highlighted]:bg-indigo-50 dark:data-[highlighted]:bg-indigo-500/10 data-[highlighted]:text-indigo-700 dark:data-[highlighted]:text-indigo-300 data-[state=checked]:font-semibold data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed`}
    >
      {checkboxStyle && (
        <span
          className={`flex size-4 shrink-0 items-center justify-center rounded border-2 transition-colors ${opt.value === value
              ? "border-indigo-500 bg-indigo-500 text-white"
              : "border-slate-400 dark:border-white/40"
            }`}
        >
          {opt.value === value && <Check className="size-3" />}
        </span>
      )}
      {opt.icon}
      <span className="flex-1 min-w-0 truncate">
        <SelectPrimitive.ItemText>{opt.label}</SelectPrimitive.ItemText>
      </span>
      {opt.badge && (
        <span className="ml-auto flex-shrink-0">
          {opt.badge}
        </span>
      )}
      {!checkboxStyle && !opt.badge && (
        <SelectPrimitive.ItemIndicator className="ml-auto flex-shrink-0">
          <Check className="size-4 text-indigo-600 dark:text-indigo-400" />
        </SelectPrimitive.ItemIndicator>
      )}
    </SelectPrimitive.Item>
  );

  if (!opt.disabledReason) {
    return item;
  }

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>{item}</TooltipTrigger>
      <TooltipContent
        hideArrow
        side="top"
        align={opt.badge ? "end" : "start"}
        collisionPadding={12}
        sideOffset={6}
        className="z-[300] max-w-xs bg-slate-900 text-white dark:bg-slate-800 border border-slate-700/80 shadow-2xl p-2.5 text-xs rounded-lg pointer-events-none flex items-start gap-2"
      >
        <AlertTriangle className="size-3.5 text-amber-400 shrink-0 mt-0.5" />
        <span className="leading-snug">{opt.disabledReason}</span>
      </TooltipContent>
    </Tooltip>
  );
}

export function Dropdown({
  value,
  onChange,
  options,
  placeholder = "Select…",
  showPlaceholderWhenEmpty = false,
  leadingLabel,
  className = "",
  placeholderClassName = "",
  disabled,
  size = "md",
  align = "start",
  variant = "pill",
  searchable = false,
  checkboxStyle = false,
  loading = false,
  chunkSize = 50,
  error = false,
  clearable = false,
}: DropdownProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const [portalContainer, setPortalContainer] = React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    if (triggerRef.current) {
      const dialog = triggerRef.current.closest<HTMLElement>(
        '[role="dialog"], [data-slot="sheet-content"]'
      );
      if (dialog) setPortalContainer(dialog);
    }
  }, []);
  const filteredOptions = React.useMemo(() => {
    const q = searchable ? query.trim().toLowerCase() : "";
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable]);

  const [visibleCount, setVisibleCount] = React.useState(chunkSize);
  React.useEffect(() => {
    setVisibleCount(chunkSize);
  }, [query, chunkSize, options.length]);
  const selectedIndex = React.useMemo(
    () => filteredOptions.findIndex((o) => o.value === value),
    [filteredOptions, value],
  );
  const renderCount = Math.max(visibleCount, selectedIndex + 1);
  const visibleOptions = React.useMemo(
    () => filteredOptions.slice(0, renderCount),
    [filteredOptions, renderCount],
  );
  const hasMore = renderCount < filteredOptions.length;
  const handleListScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!hasMore) return;
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 48) {
      setVisibleCount((n) => n + chunkSize);
    }
  };
  const hasEmptyOption = React.useMemo(
    () => options.some((o) => o.value === ""),
    [options],
  );
  const selected = React.useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );
  const showEmptyAsPlaceholder = showPlaceholderWhenEmpty && value === "";
  const handleDeselect = React.useCallback(() => {
    onChange("");
    setOpen(false);
  }, [onChange]);

  const radixValue =
    value === ""
      ? hasEmptyOption && !showEmptyAsPlaceholder
        ? EMPTY_VALUE_TOKEN
        : ""
      : value;

  const errorClass = error
    ? "!border-red-500 dark:!border-red-500 hover:!border-red-500 dark:hover:!border-red-500 focus-visible:!border-red-500 dark:focus-visible:!border-red-500 focus-visible:!ring-red-500/10"
    : "";

  return (
    <SelectPrimitive.Root
      value={radixValue}
      onValueChange={(v: string) => onChange(fromRadix(v))}
      disabled={disabled}
      open={open}
      onOpenChange={(next: boolean) => {
        setOpen(next);
        if (!next && searchable) setQuery("");
        if (next && triggerRef.current) {
          const dialog = triggerRef.current.closest<HTMLElement>(
            '[role="dialog"], [data-slot="sheet-content"]'
          );
          if (dialog) setPortalContainer(dialog);
        }
      }}
    >
      <SelectPrimitive.Trigger
        ref={triggerRef}
        className={`group inline-flex min-w-[160px] items-center justify-between gap-2 outline-none transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer data-[placeholder]:d ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${errorClass} ${className}`}
      >
        <span className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden whitespace-nowrap">
          {leadingLabel && (
            <span className="text-sm d shrink-0">{leadingLabel}</span>
          )}
          {!showEmptyAsPlaceholder && selected?.icon}
          {selected && !showEmptyAsPlaceholder ? (
            <SelectedLabel label={selected.label} className="min-w-0 flex-1 truncate whitespace-nowrap text-left" />
          ) : (
            <span className="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap text-left">
              <SelectPrimitive.Value
                placeholder={placeholder}
                className={`block max-w-full truncate whitespace-nowrap ${placeholderClassName}`}
              />
            </span>
          )}
        </span>
        {clearable && value && !disabled && (
          <span
            role="button"
            tabIndex={-1}
            aria-label="Clear selection"
            data-testid="dropdown-clear-button"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              e.preventDefault();
              onChange("");
              setOpen(false);
            }}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === "Enter" || e.key === " ") {
                e.stopPropagation();
                e.preventDefault();
                onChange("");
                setOpen(false);
              }
            }}
            className="grid size-5 place-items-center rounded text-muted-foreground hover:text-foreground focus-visible:outline-none cursor-pointer z-10 shrink-0"
          >
            <X className="size-3.5" aria-hidden />
          </span>
        )}
        <SelectPrimitive.Icon asChild>
          {loading ? (
            <Loader2 className="size-4 d animate-spin" />
          ) : (
            <ChevronDown className="size-4 d transition-transform duration-200 group-data-[state=open]:rotate-180" />
          )}
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>

      <SelectPrimitive.Portal container={portalContainer || undefined}>
        <SelectPrimitive.Content
          position="popper"
          sideOffset={6}
          align={align}
          onWheel={(e: React.WheelEvent) => e.stopPropagation()}
          className="z-[200] flex flex-col min-w-[var(--radix-select-trigger-width)] w-max max-w-[min(22rem,calc(100vw-1rem))] max-h-[320px] overflow-hidden rounded-xl border border-border bg-popover text-popover-foreground shadow-xl will-change-transform data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=open]:duration-150 data-[state=closed]:duration-100"
        >
          {searchable && (
            <div className="p-1.5 border-b border-border shrink-0">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search"
                className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-md px-2.5 py-1.5 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 text-[#0f172a] dark:text-white placeholder:text-[#64748b] dark:placeholder:text-white/25"
              />
            </div>
          )}
          <SelectPrimitive.Viewport asChild>
            <div
              onScroll={handleListScroll}
              onWheel={(e: React.WheelEvent) => e.stopPropagation()}
              className="p-1 flex-1 min-h-0 max-h-[260px] overflow-y-auto [scrollbar-width:thin] [scrollbar-color:rgba(100,116,139,0.7)_rgba(148,163,184,0.18)] [&::-webkit-scrollbar]:w-[10px] [&::-webkit-scrollbar-track]:bg-slate-400/15 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:my-1 [&::-webkit-scrollbar-thumb]:bg-slate-400/70 dark:[&::-webkit-scrollbar-thumb]:bg-slate-500/70 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:border-2 [&::-webkit-scrollbar-thumb]:border-solid [&::-webkit-scrollbar-thumb]:border-transparent [&::-webkit-scrollbar-thumb]:bg-clip-content [&::-webkit-scrollbar-thumb]:hover:bg-slate-500 dark:[&::-webkit-scrollbar-thumb]:hover:bg-slate-400">
              {loading ? (
                <div className="flex items-center justify-center gap-2 px-2.5 py-3 text-sm d">
                  <Loader2 className="size-4 animate-spin" />
                  Loading
                </div>
              ) : searchable && filteredOptions.length === 0 ? (
                <div className="px-2.5 py-3 text-sm text-center d">No matches</div>
              ) : (
                visibleOptions.map((opt) => (
                  <DropdownItem
                    key={opt.value}
                    opt={opt}
                    value={value}
                    checkboxStyle={checkboxStyle}
                    clearable={clearable}
                    onDeselect={handleDeselect}
                  />
                ))
              )}
              {!loading && hasMore && (
                <div className="flex items-center justify-center gap-2 px-2.5 py-2 text-xs d">
                  <Loader2 className="size-3 animate-spin" />
                  Loading more
                </div>
              )}
            </div>
          </SelectPrimitive.Viewport>
        </SelectPrimitive.Content>
      </SelectPrimitive.Portal>
    </SelectPrimitive.Root>
  );
}

export interface MultiSelectDropdownProps {
  values: string[];
  onChange: (values: string[]) => void;
  options: readonly DropdownOption[];
  placeholder?: string;
  leadingLabel?: string;
  className?: string;
  disabled?: boolean;
  size?: "sm" | "md";
  variant?: "pill" | "form";
  clearLabel?: string;
  selectAll?: boolean;
  searchable?: boolean;
  onOpen?: () => void;
}

export function MultiSelectDropdown({
  values,
  onChange,
  options,
  placeholder = "Select",
  leadingLabel,
  className = "",
  disabled = false,
  size = "md",
  variant = "pill",
  clearLabel = "All",
  selectAll = false,
  searchable = false,
  onOpen,
}: MultiSelectDropdownProps) {
  /* With selectAll on, the top row reports how much of the list is picked
     rather than standing for "no filter". */
  const allSelected = options.length > 0 && options.every((opt) => values.includes(opt.value));
  const someSelected = !allSelected && values.length > 0;

  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState(""); // <-- Added state for search
  const wrapRef = React.useRef<HTMLDivElement>(null);

  // Filter options based on search query
  const filteredOptions = React.useMemo(() => {
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable]);

  React.useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  // Reset query when dropdown closes
  React.useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const toggle = (value: string) => {
    onChange(
      values.includes(value)
        ? values.filter((v) => v !== value)
        : [...values, value],
    );
  };

  const summary =
    values.length === 0
      ? null
      : values.length === 1
        ? (options.find((o) => o.value === values[0])?.label ?? values[0])
        : `${values.length} selected`;

  return (
    <div ref={wrapRef} className={`relative inline-block w-full ${open ? "z-50" : ""}`}>
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!open && onOpen) onOpen();
          setOpen((o) => !o);
        }}
        className={`group inline-flex min-w-[160px] w-full items-center justify-between gap-2 outline-none transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${VARIANT_CLASS[variant]} ${SIZE_CLASS[size]} ${className}`}
      >
        <span className="flex items-center gap-2 min-w-0 flex-1">
          {leadingLabel && (
            <span className="text-sm d shrink-0">{leadingLabel}</span>
          )}
          <span className={`truncate ${summary ? "" : "d"}`}>
            {summary ?? placeholder}
          </span>
        </span>
        <ChevronDown
          className={`size-4 d transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-multiselectable
          className="absolute left-0 top-full z-50 mt-1 flex max-h-72 min-w-full flex-col overflow-hidden rounded-lg border border-border bg-card py-1 shadow-lg"
        >
          {/* Search Input */}
          {searchable && (
            <div className="border-b border-border p-1.5 shrink-0">
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.stopPropagation()}
                placeholder="Search"
                className="w-full rounded-md border border-[#e2e8f0] bg-[#f5f7fb] px-2.5 py-1.5 text-sm text-[#0f172a] outline-none placeholder:text-[#64748b] focus:border-indigo-500 dark:border-white/[0.07] dark:bg-white/[0.04] dark:text-white dark:placeholder:text-white/25 dark:focus:border-indigo-500"
              />
            </div>
          )}

          {/* Scrollable List */}
          <div className="flex-1 overflow-y-auto">
            <button
              type="button"
              onClick={() =>
                selectAll
                  ? onChange(allSelected ? [] : options.map((opt) => opt.value))
                  : onChange([])
              }
              className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] ${
                (selectAll ? allSelected : values.length === 0)
                  ? "font-semibold text-indigo-600 dark:text-indigo-400"
                  : "text-foreground"
              }`}
            >
              <span
                className={`flex size-4 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                  (selectAll ? allSelected || someSelected : values.length === 0)
                    ? "border-indigo-500 bg-indigo-600 text-white"
                    : "border-slate-400 dark:border-white/40"
                }`}
              >
                {(selectAll ? allSelected : values.length === 0) ? (
                  <Check className="size-3" />
                ) : selectAll && someSelected ? (
                  <Minus className="size-3" />
                ) : null}
              </span>
              {clearLabel}
            </button>

            {filteredOptions.length === 0 ? (
              <div className="px-3 py-3 text-center text-sm text-muted-foreground">
                No matches
              </div>
            ) : (
              filteredOptions.map((opt) => {
                const checked = values.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={checked}
                    disabled={opt.disabled}
                    onClick={() => toggle(opt.value)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-[#f1f5f9] dark:hover:bg-white/[0.05] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span
                      className={`flex size-4 shrink-0 items-center justify-center rounded border-2 transition-colors ${
                        checked
                          ? "border-indigo-500 bg-indigo-600 text-white"
                          : "border-slate-400 dark:border-white/40"
                      }`}
                    >
                      {checked && <Check className="size-3" />}
                    </span>
                    <span className="truncate flex-1">{opt.label}</span>
                    {opt.badge && <span className="ml-auto flex-shrink-0">{opt.badge}</span>}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
